import * as React from 'react'
import { ChevronRight as CaretIcon } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import type { PlantSessionMarkView } from '@axilon-apps/domains-ontology-ui-state'
import { Empty } from '../../components/primitives/Empty'
import { cn } from '../../lib/utils'
import { designPixelsToRem } from '../../lib/ui-density'
import { CollapsibleContent } from './CollapsibleContent'
import {
  SessionCompactDeletedBadge,
  SessionOtherSectionBadge,
  SessionRollupPill,
  SessionRowEndMark
} from './session-mark-ui'

/**
 * Shared left-rail tree sidebar for the Plant view master–detail tabs
 * (Processes, PLC, Robotics). Owns everything the tabs must render
 * identically — aside chrome, rail header, row layout and indent, caret
 * affordance, selection highlight, loading/empty shells, and the
 * query-param click intercept — while each tab's `*TreeNav` adapter maps
 * its typed tree nodes onto {@link PlantTreeNavNode}s. Icon, count,
 * pill, child groups, and the expansion default are the only per-tab
 * differences.
 *
 * Selection is URL-driven: every row is a `<Link>` to its canonical
 * href, and plain left-clicks rewrite `?<selectParam>=<id>` in place —
 * the first selection pushes a history entry (so back returns to the
 * unselected index), subsequent selections replace (so back doesn't walk
 * through every visited node). Modifier- and middle-clicks fall through
 * to the native `<Link>` so "open in new tab" still works.
 *
 * Expansion is component-owned. Rows with children start open or closed
 * per `defaultExpanded`, and the ancestors of `selectedId` are forced
 * open whenever the selection changes so deep links and detail-panel
 * cross-links always reveal the selected row.
 */

type IconComponent = React.ComponentType<{ size?: number | string; className?: string }>

export interface PlantTreeNavNode {
  /** Graph id — becomes the `?<selectParam>=` value and the React key. */
  id: string
  /** Canonical href, kept live for modifier-click / open-in-new-tab. */
  href: string
  label: string
  /** Kind glyph rendered before the label. Omitted → no icon column. */
  icon?: IconComponent
  /** Trailing mono count badge. Omitted → nothing rendered. */
  count?: number
  /** Amber pill text rendered after the label (e.g. "review"). */
  pill?: string
  /**
   * Session-paint mark for this row. Omitted → no paint chrome (callers that
   * don't pass a mark keep today's appearance).
   */
  mark?: PlantSessionMarkView
  /**
   * Added-descendant rollup for this container. Omitted / 0 → no rollup pill.
   * Tab adapters fill this from `rollupByContainerId` once they consume paint.
   */
  rollupCount?: number
  /**
   * Deleted children of this node (session ghosts). Rendered under the row
   * when expanded, after live child groups. Omitted → no ghost chrome.
   */
  ghosts?: PlantTreeNavGhost[]
  /**
   * Child groups. A single unlabeled group is plain nesting; labeled
   * groups render a subsection header row above their rows (the PLC
   * tab's "Programs" / "AOIs" sections).
   */
  groups: PlantTreeNavGroup[]
}

export interface PlantTreeNavGhost {
  id: string
  name: string
  view: Extract<PlantSessionMarkView, 'deleted' | 'dimmed'>
}

export interface PlantTreeNavGroup {
  label?: string
  nodes: PlantTreeNavNode[]
}

/**
 * A collapsible top-level section (a "dropdown"). When a tab passes
 * `sections`, the rail renders one caret-toggled header per section instead
 * of a flat root list — used by the Network tab to split its two root kinds
 * (Assets / Network segments) into separate dropdowns. The header is not a
 * selectable node; `id` is only an expansion + React key.
 */
export interface PlantTreeNavSection {
  id: string
  label: string
  nodes: PlantTreeNavNode[]
  /** Deleted root-level ghosts rendered at the end of this section. */
  ghosts?: PlantTreeNavGhost[]
}

export interface PlantTreeNavProps {
  heading: string
  nodes: PlantTreeNavNode[]
  /**
   * Optional collapsible top-level sections. When provided, the rail renders
   * these instead of `nodes` as a flat root list (`nodes` is ignored). Each
   * section is a caret-toggled dropdown over its own root rows.
   */
  sections?: PlantTreeNavSection[]
  /** Query param rewritten on plain-click selection ('process' | 'node'). */
  selectParam: string
  selectedId?: string
  isLoading?: boolean
  loadingText: string
  emptyTitle: string
  emptyHint: string
  /** Whether rows with children start open. */
  defaultExpanded: boolean
  /** Deleted root-level nodes with no containment parent. */
  rootGhosts?: PlantTreeNavGhost[]
  className?: string
}

export interface ParentIdTreeRow<T> {
  node: T
  children: ParentIdTreeRow<T>[]
}

/**
 * Lift a flat node list into a tree by parent id. Nodes whose parent id
 * is null or references a node not in the list become roots, so
 * malformed edges surface as extra roots instead of silently dropped
 * rows. Sibling order follows the input list order (the plant tree
 * queries sort server-side).
 */
export function buildTreeByParentId<T>(
  nodes: readonly T[],
  getId: (node: T) => string,
  getParentId: (node: T) => string | null
): ParentIdTreeRow<T>[] {
  const byId = new Map<string, ParentIdTreeRow<T>>()
  for (const node of nodes) byId.set(getId(node), { node, children: [] })
  const roots: ParentIdTreeRow<T>[] = []
  for (const row of byId.values()) {
    const parentId = getParentId(row.node)
    if (!parentId) {
      roots.push(row)
      continue
    }
    const parent = byId.get(parentId)
    if (parent) parent.children.push(row)
    else roots.push(row)
  }
  return roots
}

/** Proper ancestors of `targetId` in root-first order, or null if absent. */
function findAncestorIds(nodes: readonly PlantTreeNavNode[], targetId: string): string[] | null {
  for (const node of nodes) {
    if (node.id === targetId) return []
    for (const group of node.groups) {
      const found = findAncestorIds(group.nodes, targetId)
      if (found) return [node.id, ...found]
    }
  }
  return null
}

/**
 * Section id + ancestors that must open to reveal `targetId`, or null if the
 * node is not inside any section. Includes the containing section id even when
 * the node is a direct section root (so the dropdown opens on deep link).
 */
function findAncestorIdsInSections(
  sections: readonly PlantTreeNavSection[],
  targetId: string
): string[] | null {
  for (const section of sections) {
    const found = findAncestorIds(section.nodes, targetId)
    if (found) return [section.id, ...found]
  }
  return null
}

interface RowContext {
  selectParam: string
  selectedId?: string
  defaultExpanded: boolean
  /** Ids whose caret state differs from `defaultExpanded` (see below). */
  toggled: Set<string>
  onToggle: (id: string) => void
}

export function PlantTreeNav({
  heading,
  nodes,
  sections,
  selectParam,
  selectedId,
  isLoading,
  loadingText,
  emptyTitle,
  emptyHint,
  defaultExpanded,
  rootGhosts,
  className
}: PlantTreeNavProps): React.ReactElement {
  // Expansion is stored as the set of ids *toggled away from the
  // default* rather than an absolute open-set, so nodes that stream in
  // after mount (the tree query resolving) get `defaultExpanded` without
  // any re-seeding.
  const [toggled, setToggled] = React.useState<Set<string>>(() => new Set())

  const onToggle = React.useCallback((id: string) => {
    setToggled((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Reveal the selection whenever it changes: force every ancestor open
  // by clearing/setting its toggle relative to the default. Runs on
  // `nodes` too so a deep link that lands before the tree data does
  // still opens the path once rows exist.
  React.useEffect(() => {
    if (!selectedId) return
    const openIds = sections
      ? findAncestorIdsInSections(sections, selectedId)
      : findAncestorIds(nodes, selectedId)
    if (!openIds || openIds.length === 0) return
    setToggled((prev) => {
      let changed = false
      const next = new Set(prev)
      for (const id of openIds) {
        const open = defaultExpanded ? !next.has(id) : next.has(id)
        if (!open) {
          changed = true
          if (defaultExpanded) next.delete(id)
          else next.add(id)
        }
      }
      return changed ? next : prev
    })
  }, [nodes, sections, selectedId, defaultExpanded])

  const asideClass = cn(
    'sticky top-4 self-start rounded-xl border bg-card p-2 text-card-foreground shadow-sm',
    className
  )

  const topLevelCount = sections
    ? sections.reduce((total, section) => total + section.nodes.length, 0)
    : nodes.length

  if (isLoading && topLevelCount === 0) {
    return (
      <aside className={asideClass} aria-label={heading}>
        <TreeRailHeader heading={heading} />
        <div className="px-2 py-3 text-11 text-ink-3">{loadingText}</div>
      </aside>
    )
  }

  const hasRootGhosts = (rootGhosts?.length ?? 0) > 0
  const hasSectionGhosts = sections?.some((section) => (section.ghosts?.length ?? 0) > 0) ?? false
  if (topLevelCount === 0 && !hasRootGhosts && !hasSectionGhosts) {
    return (
      <aside className={asideClass} aria-label={heading}>
        <TreeRailHeader heading={heading} />
        <Empty title={emptyTitle} hint={emptyHint} className="px-2 py-4" />
      </aside>
    )
  }

  const ctx: RowContext = { selectParam, selectedId, defaultExpanded, toggled, onToggle }

  return (
    <aside className={asideClass} aria-label={heading}>
      <TreeRailHeader heading={heading} />
      <ul className="m-0 list-none p-0">
        {sections
          ? sections.map((section) => (
              <TreeNavSection key={section.id} section={section} ctx={ctx} />
            ))
          : nodes.map((node) => <TreeNavRow key={node.id} node={node} depth={0} ctx={ctx} />)}
        {rootGhosts?.map((ghost) => (
          <TreeNavGhostRow key={ghost.id} ghost={ghost} depth={0} />
        ))}
      </ul>
    </aside>
  )
}

function TreeNavSection({
  section,
  ctx
}: {
  section: PlantTreeNavSection
  ctx: RowContext
}): React.ReactElement {
  const hasGhosts = (section.ghosts?.length ?? 0) > 0
  const hasChildren = section.nodes.length > 0 || hasGhosts
  const open =
    hasChildren &&
    (ctx.defaultExpanded ? !ctx.toggled.has(section.id) : ctx.toggled.has(section.id))

  return (
    <li>
      <SectionHeaderRow
        label={section.label}
        count={section.nodes.length}
        hasChildren={hasChildren}
        open={open}
        onToggle={() => ctx.onToggle(section.id)}
      />
      {hasChildren && (
        <CollapsibleContent open={open}>
          <ul className="m-0 list-none p-0">
            {section.nodes.map((child) => (
              <TreeNavRow key={child.id} node={child} depth={1} ctx={ctx} />
            ))}
            {section.ghosts?.map((ghost) => (
              <TreeNavGhostRow key={ghost.id} ghost={ghost} depth={1} />
            ))}
          </ul>
        </CollapsibleContent>
      )}
    </li>
  )
}

function SectionHeaderRow({
  label,
  count,
  hasChildren,
  open,
  onToggle
}: {
  label: string
  count: number
  hasChildren: boolean
  open: boolean
  onToggle: () => void
}): React.ReactElement {
  const caret = (
    <span className="grid size-4 shrink-0 place-items-center" aria-hidden>
      {hasChildren ? (
        <CaretIcon
          size={12}
          className={cn('transition-transform duration-200', open && 'rotate-90')}
        />
      ) : null}
    </span>
  )
  const labelText = <span className="min-w-0 flex-1 truncate">{label}</span>
  const countText = <span className="font-mono text-10 tabular-nums text-ink-3">{count}</span>

  if (!hasChildren) {
    return (
      <div className="flex items-center gap-1 px-2 py-1 text-11 font-semibold uppercase tracking-[0.04em] text-ink-4">
        {caret}
        {labelText}
        {countText}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center gap-1 rounded-md bg-transparent px-2 py-1 text-left text-11 font-semibold uppercase tracking-[0.04em] text-ink-3 hover:bg-accent hover:text-accent-foreground"
    >
      {caret}
      {labelText}
      {countText}
    </button>
  )
}

function TreeRailHeader({ heading }: { heading: string }): React.ReactElement {
  return (
    <div className="px-2 pt-1 pb-2 text-10 font-semibold uppercase tracking-[0.04em] text-ink-3">
      {heading}
    </div>
  )
}

function TreeNavRow({
  node,
  depth,
  ctx
}: {
  node: PlantTreeNavNode
  depth: number
  ctx: RowContext
}): React.ReactElement {
  const hasLiveChildren = node.groups.some((group) => group.nodes.length > 0)
  const hasGhosts = (node.ghosts?.length ?? 0) > 0
  const hasChildren = hasLiveChildren || hasGhosts
  const open =
    hasChildren && (ctx.defaultExpanded ? !ctx.toggled.has(node.id) : ctx.toggled.has(node.id))

  return (
    <li>
      <TreeNavRowLink node={node} depth={depth} hasChildren={hasChildren} open={open} ctx={ctx} />
      {hasChildren && (
        <CollapsibleContent open={open}>
          <ul className="m-0 list-none p-0">
            {node.groups.map((group, index) => (
              <React.Fragment key={group.label ?? index}>
                {group.label && group.nodes.length > 0 && (
                  <SubsectionLabel
                    label={group.label}
                    depth={depth + 1}
                    count={group.nodes.length}
                  />
                )}
                {group.nodes.map((child) => (
                  <TreeNavRow key={child.id} node={child} depth={depth + 1} ctx={ctx} />
                ))}
              </React.Fragment>
            ))}
            {node.ghosts?.map((ghost) => (
              <TreeNavGhostRow key={ghost.id} ghost={ghost} depth={depth + 1} />
            ))}
          </ul>
        </CollapsibleContent>
      )}
    </li>
  )
}

function TreeNavGhostRow({
  ghost,
  depth
}: {
  ghost: PlantTreeNavGhost
  depth: number
}): React.ReactElement {
  const dimmed = ghost.view === 'dimmed'
  return (
    <li>
      <div
        className={cn(
          'flex cursor-not-allowed items-center gap-1 rounded-md px-2 py-1 text-13',
          dimmed ? 'opacity-60 text-ink-4' : 'text-st-down'
        )}
        style={{ paddingLeft: designPixelsToRem(8 + depth * 14) }}
        title={dimmed ? 'changed by another section' : 'deleted this session'}
      >
        <span className="inline-block size-4 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate font-medium line-through">{ghost.name}</span>
        <TreeNavMarkSlot>
          {dimmed ? <SessionOtherSectionBadge /> : <SessionCompactDeletedBadge />}
        </TreeNavMarkSlot>
      </div>
    </li>
  )
}

/** Far-right mark column — right-edges of `new` / `updated` / `deleted` share a line. */
function TreeNavMarkSlot({ children }: { children?: React.ReactNode }): React.ReactElement {
  return (
    <span className="inline-flex h-4 w-[4.5rem] shrink-0 items-center justify-end">{children}</span>
  )
}

function TreeNavCaretSlot({
  hasChildren,
  open,
  onToggle
}: {
  hasChildren: boolean
  open: boolean
  onToggle: (event: React.MouseEvent) => void
}): React.ReactElement {
  if (!hasChildren) {
    return <span className="inline-block size-4 shrink-0" aria-hidden />
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={open ? 'Collapse children' : 'Expand children'}
      aria-expanded={open}
      className="grid size-4 shrink-0 place-items-center rounded-sm bg-transparent p-0 text-current"
    >
      <CaretIcon
        size={12}
        className={cn('transition-transform duration-200', open && 'rotate-90')}
      />
    </button>
  )
}

function SubsectionLabel({
  label,
  depth,
  count
}: {
  label: string
  depth: number
  count: number
}): React.ReactElement {
  return (
    <li
      className="flex items-center gap-2 px-2 py-1 text-10 font-semibold uppercase tracking-[0.06em] text-ink-3"
      style={{ paddingLeft: designPixelsToRem(8 + depth * 14) }}
    >
      <span>{label}</span>
      <span className="font-mono text-10 text-ink-3">{count}</span>
    </li>
  )
}

function TreeNavRowLink({
  node,
  depth,
  hasChildren,
  open,
  ctx
}: {
  node: PlantTreeNavNode
  depth: number
  hasChildren: boolean
  open: boolean
  ctx: RowContext
}): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams()
  const isSelected = ctx.selectedId === node.id
  const Icon = node.icon
  const { onToggle: toggle, selectParam } = ctx

  const onToggle = React.useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      toggle(node.id)
    },
    [toggle, node.id]
  )

  const onSelect = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.defaultPrevented
      ) {
        return
      }
      event.preventDefault()
      const next = new URLSearchParams(searchParams)
      next.set(selectParam, node.id)
      setSearchParams(next, { replace: Boolean(searchParams.get(selectParam)) })
    },
    [node.id, selectParam, searchParams, setSearchParams]
  )

  return (
    <Link
      to={node.href}
      onClick={onSelect}
      className={cn(
        'flex items-center gap-1 rounded-md px-2 py-1 text-13 no-underline transition-colors',
        isSelected
          ? 'bg-brand-soft font-semibold text-brand'
          : 'text-ink hover:bg-accent hover:text-accent-foreground',
        node.mark === 'dimmed' && 'opacity-60'
      )}
      style={{ paddingLeft: designPixelsToRem(8 + depth * 14) }}
      aria-current={isSelected ? 'page' : undefined}
    >
      <TreeNavCaretSlot hasChildren={hasChildren} open={open} onToggle={onToggle} />
      {Icon ? <Icon size={12} className="shrink-0 text-ink-3" /> : null}
      <span className="min-w-0 flex-1 truncate" title={node.label}>
        {node.label}
      </span>
      {node.pill ? (
        <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-10 font-semibold text-amber-700 dark:text-amber-300">
          {node.pill}
        </span>
      ) : null}
      <SessionRollupPill count={node.rollupCount} />
      <TreeNavMarkSlot>
        <SessionRowEndMark mark={node.mark} />
      </TreeNavMarkSlot>
      {typeof node.count === 'number' ? (
        <span className="font-mono text-10 tabular-nums text-ink-3">{node.count}</span>
      ) : null}
    </Link>
  )
}
